/**
 * TanStack Query hooks for traveler roster mutations and user search.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services';
import { unwrapServiceResultAsync } from '../services/utils/errorUtils';
import { SEARCH_MIN_LENGTH } from '../constants';
import { logger } from '../utils/logger';
import { TRIPS_QUERY_KEY, tripByIdQueryKey } from './useTripsQueries';

export const USER_SEARCH_QUERY_KEY = (query: string, tripId?: string) =>
  ['users', 'search', query, tripId ?? ''] as const;

export function useUserSearchQuery(query: string, tripId?: string) {
  return useQuery({
    queryKey: USER_SEARCH_QUERY_KEY(query, tripId),
    queryFn: () =>
      unwrapServiceResultAsync(apiService.travelers.searchUsers(query, tripId)),
    enabled: query.trim().length >= SEARCH_MIN_LENGTH,
  });
}

export function useAddTravelerMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (travelerId: number) =>
      unwrapServiceResultAsync(
        apiService.travelers.addTraveler(tripId, { travelerId })
      ),
    onSuccess: () => {
      logger.info('Traveler added', tripId);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}

export function useRemoveTravelerMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (travelerId: number) =>
      unwrapServiceResultAsync(
        apiService.travelers.removeTraveler(tripId, travelerId)
      ),
    onSuccess: (_data, travelerId) => {
      logger.info('Traveler removed', tripId, travelerId);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}
